import React, { useState, useEffect, useRef } from 'react';

// ── Section ID map ────────────────────────────────────────────────────────────
const SECTION_IDS = {
  'news-grid':  'news-grid',
  'video':      'video-section',
  'subscribe':  'subscribe-section',
  'about':      'about',
  'advertise':  'advertise',
  'terms':      'terms',
  'privacy':    'privacy',
  'contact':    'contact',
};

const NotificationTicker = () => {
  const [notifications, setNotifications] = useState([]);
  const [paused, setPaused]               = useState(false);
  const tickerRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        // ── SSR path — use Django-injected data, no network call needed ──
        if (window.__INITIAL_DATA__?.notifications) {
          const list = window.__INITIAL_DATA__.notifications;
          if (Array.isArray(list) && list.length > 0) {
            setNotifications(list);
            delete window.__INITIAL_DATA__.notifications;
            return;
          }
        }

        // ── Fallback — fetch from internal endpoint ──
        const res  = await fetch('/internal/notifications/');
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.results || []);
        if (list.length > 0) setNotifications(list);
      } catch (e) {
        console.error('Notification fetch error:', e);
      }
    };
    load();
  }, []);

  const handleClick = (redirectSection) => {
    const sectionId = SECTION_IDS[redirectSection] || redirectSection;
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (notifications.length === 0) return null;

  const items    = [...notifications, ...notifications, ...notifications];
  const duration = Math.max(20, notifications.length * 12);

  return (
    <div style={s.wrapper}>

      <div style={s.label}>
        <span style={s.labelDot} />
        LIVE
      </div>

      <div
        style={s.tickerOuter}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div
          ref={tickerRef}
          style={{
            ...s.tickerInner,
            animationDuration: `${duration}s`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        >
          {items.map((n, i) => (
            <span key={`${n.id}-${i}`} style={s.item}>
              <span
                style={s.text}
                onClick={() => handleClick(n.redirect_section)}
                role="button"
                tabIndex={0}
                title={`Go to ${n.redirect_section}`}
                onKeyDown={e => e.key === 'Enter' && handleClick(n.redirect_section)}
              >
                {n.text}
              </span>
              <span style={s.separator} aria-hidden="true">◆</span>
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </div>
  );
};

const s = {
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    background: '#0a1628',
    borderTop:    '1px solid rgba(220,38,38,0.3)',
    borderBottom: '1px solid rgba(220,38,38,0.15)',
    height: 40,
    overflow: 'hidden',
    userSelect: 'none',
  },
  label: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#dc2626',
    color: '#fff',
    fontSize: '0.65rem',
    fontWeight: 800,
    letterSpacing: '0.12em',
    padding: '0 14px',
    height: '100%',
    whiteSpace: 'nowrap',
  },
  labelDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 0 6px #fff',
    animation: 'pulse 1.4s ease-in-out infinite',
  },
  tickerOuter: {
    flex: 1,
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    maskImage: 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)',
    WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)',
  },
  tickerInner: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    animation: 'ticker-scroll linear infinite',
    willChange: 'transform',
  },
  item: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1.25rem',
  },
  text: {
    color: '#e2e8f0',
    fontSize: '0.82rem',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '2px 0',
    transition: 'color 0.2s',
    outline: 'none',
  },
  separator: {
    color: '#dc2626',
    fontSize: '0.55rem',
    opacity: 0.7,
    marginLeft: '1.25rem',
  },
};

export default NotificationTicker;